import React, { useCallback, useEffect, useRef, useState } from "react";
import cx from "classnames";
import styles from "./common.module.scss";
import { AnimatedHeight } from "./AnimatedHeight";
import { EmptyState, TableSkeleton } from "./states";
import { ViewToggle } from "./ViewToggle";
import { TableCellText } from "./TableCellText";
import { ChartView } from "src/types/experiences/leadsAndReports";

export type ColumnAlign = "left" | "right" | "center" | "right-fill";

export interface ColumnDef {
  key: string;
  header: React.ReactNode;
  align?: ColumnAlign;
}

const headerAlignClass = (align?: ColumnAlign): string | undefined => {
  if (align === "right" || align === "right-fill") return styles.alignRight;
  if (align === "center") return styles.alignCenter;
  return undefined;
};

const bodyAlignProps = (align?: ColumnAlign): [string, string] | null => {
  switch (align) {
    case "right":
      return ["right", "end"];
    case "right-fill":
      return ["right", "stretch"];
    case "center":
      return ["center", "center"];
    default:
      return null;
  }
};

const useRowAlignmentCss = (columns: ColumnDef[]): { scopeClass: string; css: string } => {
  const scopeClass = React.useMemo(
    () => `atbl-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );

  const css = React.useMemo(() => {
    const rules: string[] = [];
    columns.forEach((c, i) => {
      const props = bodyAlignProps(c.align);
      if (!props) return;
      const [textAlign, justifySelf] = props;
      rules.push(
        `.${scopeClass} .${styles.tableRow} > *:nth-child(${i + 1}){text-align:${textAlign};justify-self:${justifySelf};}`,
      );
    });
    return rules.join("\n");
  }, [columns, scopeClass]);

  return { scopeClass, css };
};

export const TABLE_MIN_HEIGHT = { split: 280, full: 400 } as const;

interface AnalyticsCardProps {
  title: string;
  count?: number;
  action?: React.ReactNode;
  download?: React.ReactNode;
  currentChart?: ChartView;
  disableView?: ChartView[];
  // eslint-disable-next-line no-unused-vars
  onChartChange?: (view: ChartView) => void;
  minHeight?: number;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

export const AnalyticsCard: React.FC<AnalyticsCardProps> = ({
  title,
  count,
  action,
  download,
  currentChart,
  disableView,
  onChartChange,
  minHeight,
  footer,
  children,
}) => (
  <div className={styles.analyticsCard}>
    <div className={styles.analyticsHeader}>
      <div className={styles.analyticsTitle}>
        {title}
        {count !== undefined && <span className={styles.count}>{` (${count})`}</span>}
      </div>
      <div className={styles.analyticsActions}>
        {action}
        {download}
        {currentChart && onChartChange && (
          <ViewToggle current={currentChart} disableView={disableView} onChange={onChartChange} />
        )}
      </div>
    </div>
    <AnimatedHeight className={styles.analyticsCardBody} minHeight={minHeight}>
      {children}
    </AnimatedHeight>
    {footer}
  </div>
);

const ScrollableTable: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    setShowFade(hasOverflow && !atEnd);
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [update, children]);

  return (
    <div className={styles.tableScrollWrap}>
      <div ref={ref} className={styles.tableScroll} onScroll={update}>
        {children}
      </div>
      <div className={cx(styles.scrollFade, { [styles.scrollFadeVisible]: showFade })} />
    </div>
  );
};

interface AnalyticsTableProps<T> {
  columns: ColumnDef[];
  rows: T[];
  // eslint-disable-next-line no-unused-vars
  renderRow: (row: T, index: number) => React.ReactNode;
  loading?: boolean;
  gridTemplate?: string;
  minWidth?: number | string;
  stickyFirstColumn?: boolean;
  variant?: "full" | "split";
  contentMinHeight?: number;
}

export function AnalyticsTable<T>({
  columns,
  rows,
  renderRow,
  loading,
  gridTemplate,
  minWidth,
  stickyFirstColumn,
  variant = "full",
  contentMinHeight,
}: AnalyticsTableProps<T>) {
  const template = gridTemplate ?? `repeat(${columns.length}, 1fr)`;

  const tableMinHeight = variant === "split" ? TABLE_MIN_HEIGHT.split : TABLE_MIN_HEIGHT.full;

  const minHeightFloor = contentMinHeight ?? tableMinHeight;

  const { scopeClass, css: alignCss } = useRowAlignmentCss(columns);

  if (loading) {
    return (
      <AnimatedHeight minHeight={minHeightFloor}>
        <ScrollableTable>
          <TableSkeleton
            columns={columns.length}
            rows={rows?.length || 5}
            gridTemplate={template}
            minWidth={minWidth}
          />
        </ScrollableTable>
      </AnimatedHeight>
    );
  }

  const rowStyle: React.CSSProperties = {
    gridTemplateColumns: template,
    minWidth: typeof minWidth === "number" ? `${minWidth}px` : minWidth,
  };

  const isEmpty = !rows || rows.length === 0;
  const bodyMinHeight = isEmpty ? tableMinHeight : minHeightFloor;

  return (
    <AnimatedHeight minHeight={bodyMinHeight}>
      <ScrollableTable>
        {alignCss && <style>{alignCss}</style>}
        <div className={cx(scopeClass, styles.table, { [styles.stickyFirst]: stickyFirstColumn })}>
          <div className={styles.tableHeadRow} style={rowStyle}>
            {columns.map((c) => (
              <div key={c.key} className={cx(styles.cellHead, headerAlignClass(c.align))}>
                {typeof c.header === "string" ? (
                  <TableCellText className={styles.cellHeadText} text={c.header} />
                ) : (
                  c.header
                )}
              </div>
            ))}
          </div>
          {!isEmpty &&
            rows.map((row, i) => (
              <div key={i} className={styles.tableRow} style={rowStyle}>
                {renderRow(row, i)}
              </div>
            ))
          }
        </div>
      </ScrollableTable>
      {isEmpty && (
        <div
          className={styles.emptyTableBody}
          style={{ minHeight: tableMinHeight - 48 }}
        >
          <EmptyState />
        </div>
      )}
    </AnimatedHeight>
  );
}

const CellValue: React.FC<{ value: React.ReactNode; className?: string }> = ({ value, className }) => {
  if (typeof value === "string" || typeof value === "number") {
    return <TableCellText className={className} text={String(value)} />;
  }
  return <div className={className}>{value}</div>;
};

export const Cell: React.FC<{ headline?: React.ReactNode; description?: React.ReactNode, className?: string }> = ({
  headline,
  description,
  className,
}) => (
  <div className={cx(styles.cell, className)}>
    {headline !== undefined && <CellValue value={headline} className={styles.headline} />}
    {description !== undefined && <CellValue value={description} className={styles.description} />}
  </div>
);

export const tableStyles = styles;
export { cx };
